import { forwardRef } from 'react';
import { cva } from 'class-variance-authority';
import { cn } from '../../utils/cn';
import type { HeadingProps } from './Typography.types';

const headingVariants = cva(
  'font-bold tracking-tight text-neutral-900 dark:text-neutral-100',
  {
    variants: {
      size: {
        xs: 'text-sm',
        sm: 'text-base',
        md: 'text-lg',
        lg: 'text-xl',
        xl: 'text-2xl',
        '2xl': 'text-3xl',
        '3xl': 'text-4xl',
        '4xl': 'text-5xl',
      },
    },
    defaultVariants: {
      size: 'lg',
    },
  },
);

const defaultSizeMap: Record<string, string> = {
  h1: '3xl',
  h2: '2xl',
  h3: 'xl',
  h4: 'lg',
  h5: 'md',
  h6: 'sm',
};

export const Heading = forwardRef<HTMLHeadingElement, HeadingProps>(
  ({ as: Tag = 'h2', size, className, ...props }, ref) => {
    const resolvedSize = size ?? defaultSizeMap[Tag] ?? 'lg';

    return (
      <Tag
        ref={ref}
        className={cn(
          headingVariants({
            size: resolvedSize as HeadingProps['size'],
          }),
          className,
        )}
        {...props}
      />
    );
  },
);

Heading.displayName = 'Heading';
